import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FAMILIES,
  DOMAINS,
  PRACTICES,
  PRACTICE_EMOJI,
  CATALOG_COUNT,
  familyOfRole,
  practicesIn,
  searchCatalog,
} from '../data/agentCatalog';

const SHOW_HITS = 80;

function mark(id) {
  return PRACTICE_EMOJI[id] || '✦';
}

function FamilyShape({ shape }) {
  const props = {
    className: 'aio-shape',
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.25',
    'aria-hidden': 'true',
  };
  if (shape === 'diamond') return <svg {...props}><path d="M8 1.5 14.5 8 8 14.5 1.5 8Z" /></svg>;
  if (shape === 'square') return <svg {...props}><rect x="2.5" y="2.5" width="11" height="11" /></svg>;
  if (shape === 'bars') return <svg {...props}><path d="M2 5h12M2 8h12M2 11h8" /></svg>;
  if (shape === 'triangle') return <svg {...props}><path d="M8 2 14.5 14H1.5Z" /></svg>;
  if (shape === 'hex') return <svg {...props}><path d="M8 1.5 14 5v6L8 14.5 2 11V5Z" /></svg>;
  if (shape === 'ring') return <svg {...props}><circle cx="8" cy="8" r="5.5" /><circle cx="8" cy="8" r="2" /></svg>;
  if (shape === 'pair') return <svg {...props}><circle cx="5" cy="8" r="2.4" /><circle cx="11" cy="8" r="2.4" /></svg>;
  if (shape === 'grid') {
    return (
      <svg {...props}>
        <rect x="2" y="2" width="5" height="5" />
        <rect x="9" y="2" width="5" height="5" />
        <rect x="2" y="9" width="5" height="5" />
        <rect x="9" y="9" width="5" height="5" />
      </svg>
    );
  }
  if (shape === 'cross') return <svg {...props}><circle cx="8" cy="8" r="5.5" /><path d="M8 2.5v11M2.5 8h11" /></svg>;
  return <svg {...props}><circle cx="8" cy="8" r="5.5" /></svg>;
}

export default function AgentLibraryPanel({ library, entitled, busy, onAdd, libErr }) {
  const [query, setQuery] = useState('');
  const [openFamily, setOpenFamily] = useState('');
  const [openPractice, setOpenPractice] = useState('');
  const diveRef = useRef(null);
  const roleById = useMemo(() => Object.fromEntries(library.map((role) => [role.id, role])), [library]);
  const hits = searchCatalog(query);
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const practiceHits = terms.length
    ? PRACTICES.filter((practice) => {
      const family = FAMILIES.find((item) => item.id === practice.family);
      const hay = `${practice.name} ${practice.id} ${family?.name || ''}`.toLowerCase();
      return terms.every((term) => hay.includes(term));
    })
    : [];
  const hitFamilies = useMemo(() => new Set((hits || []).map((hit) => hit.familyId)), [hits]);
  const known = new Set(FAMILIES.map((family) => family.id));
  const extras = library.filter((role) => !known.has(familyOfRole(role.id)));
  const family = FAMILIES.find((item) => item.id === openFamily) || null;

  useEffect(() => {
    if (openFamily && diveRef.current) diveRef.current.scrollIntoView({ block: 'nearest' });
  }, [openFamily]);

  const toggleFamily = (id) => {
    setOpenFamily((cur) => (cur === id ? '' : id));
    setOpenPractice('');
  };

  const openDive = (familyId, practiceId) => {
    setOpenFamily(familyId);
    setOpenPractice(practiceId);
  };

  return (
    <section className="aio" aria-label="Agent types">
      <div className="aio-head">
        <p className="cs-help">
          {FAMILIES.length} families, {PRACTICES.length} practices, {CATALOG_COUNT.toLocaleString()} domain types.
          Open a family, then a practice.
        </p>
      </div>
      <input
        className="cs-input aio-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search families, practices, domains"
        aria-label="Search agent types"
      />
      {!library.length && !libErr && <p className="cs-help">Loading prompts…</p>}
      {libErr && <p className="cs-help">{libErr}</p>}

      <div className="aio-map">
        {FAMILIES.map((item) => {
          const practices = practicesIn(item.id);
          const types = item.id === 'coordination' ? 1 : practices.length * DOMAINS.length;
          const dim = terms.length && !hitFamilies.has(item.id) && !practiceHits.some((p) => p.family === item.id);
          return (
            <article
              key={item.id}
              className={`aio-family-card${openFamily === item.id ? ' is-open' : ''}${dim ? ' is-dim' : ''}`}
            >
              <button
                type="button"
                className="aio-family-open"
                aria-expanded={openFamily === item.id}
                onClick={() => toggleFamily(item.id)}
              >
                <FamilyShape shape={item.shape} />
                <span className="aio-emoji" aria-hidden="true">{item.emoji}</span>
                <span className="aio-family-name">{item.name}</span>
                <span className="aio-line">{item.line}</span>
                <span className="aio-count">{item.id === 'coordination' ? '1 role' : `${types.toLocaleString()} types`}</span>
              </button>
              <div className="aio-chips">
                {practices.map((practice) => (
                  <button
                    key={practice.id}
                    type="button"
                    className={`aio-chip${openPractice === practice.id ? ' is-open' : ''}${practice.live ? ' is-live' : ''}`}
                    onClick={() => openDive(item.id, practice.id)}
                  >
                    <span aria-hidden="true">{mark(practice.id)}</span>
                    {practice.name}
                  </button>
                ))}
              </div>
            </article>
          );
        })}
      </div>

      {terms.length > 0 && (
        <div className="aio-hits">
          <p className="cs-help">
            {(practiceHits.length + (hits?.length || 0)).toLocaleString()} matches
            {(practiceHits.length + (hits?.length || 0)) > SHOW_HITS ? ` · showing ${SHOW_HITS}` : ''}
          </p>
          <div className="aio-domain-grid">
            {practiceHits.map((practice) => {
              const owner = FAMILIES.find((item) => item.id === practice.family);
              return (
                <button
                  key={practice.id}
                  type="button"
                  className="aio-domain"
                  onClick={() => openDive(practice.family, practice.id)}
                >
                  <span aria-hidden="true">{mark(practice.id)}</span>
                  {practice.name}
                  <span className="cs-help">{owner?.name}</span>
                </button>
              );
            })}
            {(hits || []).slice(0, Math.max(0, SHOW_HITS - practiceHits.length)).map((hit) => {
              const domain = DOMAINS.find((item) => item.id === hit.domainId);
              return (
                <button
                  key={hit.id}
                  type="button"
                  className="aio-domain"
                  onClick={() => openDive(hit.familyId, hit.practiceId)}
                >
                  <span aria-hidden="true">{domain?.emoji}</span>
                  {hit.name}
                  <span className="cs-help">{hit.family}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {family && (
        <div className="aio-dive" ref={diveRef}>
          <div className="aio-dive-head">
            <h3>
              <FamilyShape shape={family.shape} />
              <span aria-hidden="true">{family.emoji}</span>
              {family.name}
            </h3>
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => toggleFamily(family.id)}>
              Close
            </button>
          </div>
          <div className="aio-practice-grid">
            {practicesIn(family.id).map((practice) => {
              const role = roleById[practice.id];
              const open = openPractice === practice.id;
              const domains = practice.id === 'coordinator' ? [] : DOMAINS;
              return (
                <article
                  key={practice.id}
                  className={`aio-practice${open ? ' is-open' : ''}`}
                >
                  <button
                    type="button"
                    className="aio-practice-open"
                    aria-expanded={open}
                    onClick={() => setOpenPractice(open ? '' : practice.id)}
                  >
                    <span className="aio-emoji" aria-hidden="true">{mark(practice.id)}</span>
                    <span className="aio-practice-name">{practice.name}</span>
                    {role && <span className="aio-class">{role.modelClass}</span>}
                    {practice.live && <span className="aio-live">Prompt</span>}
                  </button>
                  <p className="cs-help">{role?.blurb || practice.blurb}</p>
                  {practice.live && entitled && (
                    <button
                      type="button"
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      disabled={busy || !role}
                      onClick={() => onAdd(practice.id)}
                    >
                      Add
                    </button>
                  )}
                  {open && domains.length > 0 && (
                    <div className="aio-domain-grid">
                      {domains.map((domain) => (
                        <div key={domain.id} className="aio-domain">
                          <span aria-hidden="true">{domain.emoji}</span>
                          {domain.name} {practice.name}
                        </div>
                      ))}
                    </div>
                  )}
                  {open && practice.id === 'coordinator' && (
                    <p className="cs-help aio-note">
                      This role names the others for one objective.
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      )}

      {extras.length > 0 && (
        <div className="aio-dive">
          <h3>Also in the library</h3>
          <div className="aio-practice-grid">
            {extras.map((role) => (
              <article key={role.id} className="aio-practice">
                <strong>{role.name}</strong>
                <p className="cs-help">{role.blurb}</p>
                {entitled && (
                  <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => onAdd(role.id)}>
                    Add
                  </button>
                )}
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
